<template>
  <div class="organization-home pt-0">
    <div v-if="loading" class="loading-div">
      <v-progress-circular
        class="loading-icon"
        indeterminate
        color="primary"
      />
    </div>
    <Dialog
      :dialog.sync="deleteFieldDialog.show"
      :cardText="deleteFieldDialog.cardText"
      :mainBtnText="deleteFieldDialog.mainBtnText"
      :secondaryBtnText="deleteFieldDialog.secondaryBtnText"
      :alertColor="true"
      @mainBtnCallback="deleteField"
    />
    <v-dialog
      v-if="deviceDetailDialog"
      v-model="deviceDetailDialog"
      width="500"
      content-class="device-detail-dialog"
    >
      <v-card>
        <v-card-title class="text-h5 lighten-2 text-break">
          {{$t('OrganizationData.deviceDetail', {name: deviceDetailSelected.campaign.entityName})}}
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6" class="pt-0 pl-0">
              <p class="section-title mb-2" style="color: #000000DE">{{ $t('OrganizationData.users') }}</p>
              <template v-if="deviceDetailSelected.users.length">
                <p class="mb-0" style="color: #000000DE" v-for="user in deviceDetailSelected.users">{{ getUserFullName(user.id) }}</p>
              </template>
              <p v-else>{{ $t('OrganizationData.noAssignedUsers') }}</p>
            </v-col>
            <v-col cols="12" sm="6" class="pt-0 pl-0">
              <p class="section-title mb-2" style="color: #000000DE">{{ $t('OrganizationData.crops') }}</p>
              <template v-if="deviceDetailSelected.campaign.crops.length">
                <p class="mb-0" style="color: #000000DE" v-for="crop in deviceDetailSelected.campaign.crops">{{ crop.name }} ({{ getFieldName(crop.field) }})</p>
              </template>
              <p v-else>{{ $t('OrganizationData.noAssignedCrops') }}</p>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Pending campaign onboarding dialog -->
    <v-dialog
      v-if="pendingCampaignOnboardingDialog"
      v-model="pendingCampaignOnboardingDialog"
      width="auto"
      persistent
    >
      <div class="pending-campaign-onboarding-dialog">
        <v-card color="#EEF2F5" class="py-4 px-4" rounded="lg">
          <v-card-title class="justify-center pt-4">
            <v-img :src="require('@/assets/icons/alert.png')" max-width="1.5rem" max-height="1.5rem" />
            <p class="title-text ml-2 mb-0">{{ $t('OrganizationData.pendingCampaignOnboardingDialogTitle') }}</p>
          </v-card-title>
          <v-card-text class="body-text text-center px-6 mt-4">
            {{ $t('OrganizationData.pendingCampaignOnboardingDialogText') }}
          </v-card-text>

          <v-card-actions>
            <v-row>
              <v-col align="end">
                <v-btn
                  class="secondary-button"
                  color="#FFFFFF"
                  @click="pendingCampaignOnboardingRemindMeLater"
                >
                  {{ $t('OrganizationData.pendingCampaignOnboardingDialogRemindMeLater') }}
                </v-btn>
              </v-col>
              <v-col align="start">
                <v-btn
                  class="confirm-button"
                  color="#0098E1"
                  medium
                  @click="pendingCampaignOnboardingConfirm"
                >
                  {{ $t('OrganizationData.pendingCampaignOnboardingDialogConfirm') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </div>
    </v-dialog>

    <!-- Postponement requests dialog -->
    <PostponementRequestDialog
      v-else-if="devicesWithPendingPostponementRequests.length"
      :devices.sync="devicesWithPendingPostponementRequests"
    />

    <!-- Add user dialog -->
    <AddUserDialog
      v-if="addUserDialog"
      :dialog.sync="addUserDialog"
      :fromNewCampaign="false"
      :campaignId="currentCampaignId"
    />

    <!-- Service extension dialog -->
    <ServiceExtensionDialog
      v-if="serviceExtensionDialog.show"
      :dialog.sync="serviceExtensionDialog.show"
      :device="serviceExtensionDialog.device"
    />

    <div id="header-div" class="header-div">
      <v-row class="py-2">
        <v-col cols="12" md="6" class="py-0 px-0" :class="display === 'xs' || display === 'sm' ? 'text-center place-items-center' : ''">
          <div class="campaign-header-container">
            <h3 class="header-main" :class="display === 'xs' || display === 'sm' ? 'py-1' : 'py-3'"><b>{{$t('OrganizationData.dashboard')}}</b> {{organization}}</h3>
            <div :class="$store.getters.availableCampaigns.length ? 'campaign-selector-container' : 'campaign-selector-container-empty'">
              <p v-if="$store.getters.availableCampaigns.length" class="body-text" @click="toggleCampaignMenu">
                <b>{{$t('EditCampaign.service')}}</b>
                {{selectedCampaign ? selectedCampaign.name : ''}}
              </p>
              <p v-else class="body-text">
                <b>{{$t('EditCampaign.emptyCampaigns')}}</b>
              </p>
              <template v-if="$store.getters.availableCampaigns.length">
                <v-btn class="mx-1" text fab x-small @click="editCampaign" v-if="selectedCampaign">
                  <v-icon color="#00202F" size="16">mdi-pencil</v-icon>
                </v-btn>
                <v-btn class="mx-1" text fab x-small @click="toggleCampaignMenu">
                  <v-icon color="#00202F" size="18">{{campaignMenuExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon>
                </v-btn>
              </template>
            </div>
            <div v-show="campaignMenuExpanded" class="campaign-menu">
              <v-list dense>
                <v-list-item
                  v-for="campaign in availableCampaigns"
                  :key="campaign.id"
                  @click="selectCampaign(campaign)"
                  :class="{ 'selected-campaign': selectedCampaign && selectedCampaign.id === campaign.id }"
                >
                  <v-list-item-title>
                    <b>{{campaign.name}}</b>
                    <span v-if="campaign.fromDate" class="campaign-date">
                      ({{formatCampaignDate(campaign.fromDate, campaign.toDate)}})
                    </span>
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="6" class="py-0 px-0" style="align-self: center">
          <v-row class="section-menu-text" :class="display === 'xs' || display === 'sm' ? 'justify-content-center' : 'justify-content-right'">
            <p class="mb-0">
              <a id="devices-section-menu" style="text-decoration: underline" @click="goToSection('devices')" class="section-menu-text">{{$t('OrganizationData.devices')}}</a> | <a id="fields-section-menu" @click="goToSection('fields')" class="section-menu-text">{{$t('OrganizationData.fields')}}</a> | <a id="users-section-menu" @click="goToSection('users')" class="section-menu-text">{{$t('OrganizationData.users')}}</a>
            </p>
          </v-row>
        </v-col>
      </v-row>
      <v-divider />
    </div>
    <v-container class="organization-data px-0" id="organization-data">
      <div id="finish-campaign-onboarding-section" v-if="showFinishCampaignOnboardingCard()">
        <v-row>
          <v-col cols="12">
            <v-card class="finish-campaign-onboarding-card px-2">
              <v-card-title>
                <p class="title-text mb-0">{{ $t('OrganizationData.finishCampaignOnboarding.title') }}</p>
              </v-card-title>
              <v-card-text>
                <ol>
                  <v-row v-if="devicesWithMissingData.length" class="mb-0 mt-1">
                    <li class="body-text my-1 mr-1">{{ $t('OrganizationData.finishCampaignOnboarding.requiredDevicesData') }}</li>
                    <v-row>
                      <div class="device-with-missing-data-card mx-1 my-1" v-for="devWithMissingData in limitedDevicesWithMissingData">
                        <p class="mb-0 mx-2">{{ devWithMissingData.campaign.entityName }}</p>
                      </div>
                      <p v-if="devicesWithMissingData.length > DEVICES_WITH_MISSING_DATA_TO_SHOW" class="mb-0 ellipsis-text">...</p>
                    </v-row>
                  </v-row>
                  <v-row v-if="devicesWithoutAssignedCrops.length" class="mb-0 mt-1">
                    <li class="body-text my-1 mr-1">{{ $t('OrganizationData.finishCampaignOnboarding.addFieldsAndCrops') }}</li>
                    <v-row>
                      <div class="device-with-missing-data-card mx-1 my-1" v-for="devWithoutCrops in limitedDevicesWithoutCrops">
                        <p class="mb-0 mx-2">{{ devWithoutCrops.campaign.entityName }}</p>
                      </div>
                      <p v-if="devicesWithoutAssignedCrops.length > DEVICES_WITHOUT_CROPS_TO_SHOW" class="mb-0 ellipsis-text">...</p>
                    </v-row>
                  </v-row>
                  <v-row v-if="devicesWithoutAssignedUsers.length" class="mb-0 mt-1">
                    <li class="body-text my-1 mr-1">{{ $t('OrganizationData.finishCampaignOnboarding.addUsers') }}</li>
                    <v-row>
                      <div class="device-with-missing-data-card mx-1 my-1" v-for="devWithoutUsers in limitedDevicesWithoutUsers">
                        <p class="mb-0 mx-2">{{ devWithoutUsers.campaign.entityName }}</p>
                      </div>
                      <p v-if="devicesWithoutAssignedUsers.length > DEVICES_WITHOUT_USERS_TO_SHOW" class="mb-0 ellipsis-text">...</p>
                    </v-row>
                  </v-row>
                </ol>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <div id="services-soon-to-end-section" v-if="devicesNearEndOfService.length">
         <v-row>
           <v-col cols="12">
             <v-card class="finish-campaign-onboarding-card px-2">
               <v-card-title>
                 <p class="title-text mb-0">{{ $t('OrganizationData.servicesSoonToEnd.title') }}</p>
               </v-card-title>
               <v-card-text>
                 <div class="d-flex align-center flex-wrap mb-3">
                   <p class="body-text mb-0 mr-3">{{ $t('OrganizationData.servicesSoonToEnd.mainDescription') }}</p>
                   <div class="device-with-missing-data-card mx-1 my-1" v-for="device in limitedDevicesNearEndOfService" :key="device.id">
                     <p class="mb-0 mx-2">{{ device.campaign.entityName }}</p>
                   </div>
                   <p v-if="devicesNearEndOfService.length > DEVICES_NEAR_END_OF_SERVICE_TO_SHOW" class="mb-0 ellipsis-text">...</p>
                 </div>
                 <p class="body-text mb-0">{{ $t('OrganizationData.servicesSoonToEnd.extensionInfo') }}</p>
               </v-card-text>
             </v-card>
           </v-col>
         </v-row>
       </div>
      <div id="devices-section">
        <v-row class="section-header pt-5 px-3">
          <p class="section-title">{{$t('OrganizationData.devices')}}</p>
        </v-row>
        <v-layout row wrap class="devices-list">
          <template v-if="devices.length">
            <v-flex v-for="dev in devices" :key="dev.id" xs12 sm6 md3 class="px-3 py-3">
              <div :class="['device-container', { 'with-banner': isNearEndOfService(dev) }]">
                <div v-if="isNearEndOfService(dev)" class="monitoring-finalize-banner">
                  <p class="monitoring-finalize-text">{{$t('OrganizationData.monitoringToFinalize')}}</p>
                </div>
                <v-card class="device-card">
                  <v-card-title>
                    <v-layout>
                      <v-flex xs2>
                        <v-img :src="require('@/assets/min-irrigation.png')"/>
                      </v-flex>
                      <v-flex xs8 class='px-1'>
                        <p class="device-header-title ma-0">{{dev.campaign.entityName}}</p>
                        <p class="device-header-subtitle ma-0">{{getDeviceFieldName(dev)}}</p>
                        <p class="device-header-subtitle ma-0" style="font-size: 13px">{{getEquipmentType(dev.equipment.type)}}<span v-if="isUserAdmin"> - ID: {{dev.id}}</span></p>
                        <p v-if="dev.equipment.type == EQUIPMENT_TYPE.AF" class="device-header-subtitle ma-0" style="font-size: 14px">{{getLinearEquipmentSubtype(dev.equipment.subtype)}}</p>
                      </v-flex>
                      <v-flex xs2>
                        <v-menu offset-y left>
                          <template v-slot:activator="{ on }">
                            <v-btn class="menu-btn" dark text icon color="#00202F" v-on="on" @click="openDeviceMenu(dev)">
                              <v-icon>more_horiz</v-icon>
                            </v-btn>
                          </template>
                          <v-list class="pa-0">
                            <v-list-item v-if="canEditDevice(dev)" @click="editDevice(dev)">
                              <v-list-item-title>{{ $t('OrganizationData.edit') }}</v-list-item-title>
                            </v-list-item>
                            <template v-if="isNearEndOfService(dev)">
                              <v-divider class="mx-2" />
                              <v-list-item @click="queryServiceExtension(dev)">
                                <v-list-item-title class="device-header-extension-btn">{{ $t('OrganizationData.queryServiceExtension') }}</v-list-item-title>
                              </v-list-item>
                            </template>
                          </v-list>
                        </v-menu>
                      </v-flex>
                    </v-layout>
                  </v-card-title>
                  <v-card-text>
                    <p class="mb-1" style="color: #F93A2F" v-if="!dev.hasCompleteData"><v-icon size="0.9rem" color="#F93A2F" class="mr-1" style="vertical-align: middle;">mdi-alert-octagon-outline</v-icon>{{$t('OrganizationData.requiredDeviceData')}}</p>
                    <p class="mb-1" :style="`cursor: pointer; color: ${dev.users.length === 0 ? '#F93A2F' : '#00202F'}`" @click="openDeviceDetailsDialog(dev)">{{ dev.users.length == 1 ? $t('OrganizationData.assignedUserQty', {users: 1}) : $t('OrganizationData.assignedUsersQty', {users: dev.users.length}) }}</p>
                    <p class="mb-1" :style="`cursor: pointer; color: ${dev.campaign.crops.length === 0 ? '#F93A2F' : '#00202F'}`" @click="openDeviceDetailsDialog(dev)">{{ dev.campaign.crops.length == 1 ? $t('OrganizationData.deviceCropQty', {crops: 1}) : $t('OrganizationData.deviceCropsQty', {crops: dev.campaign.crops.length}) }}</p>
                    <a
                      v-if="deviceActivePluviometricChartFile(dev)"
                      class="device-sheet-link"
                      @click="downloadPluviometricChart(deviceActivePluviometricChartFile(dev), dev)"
                    >
                      {{$t('OrganizationData.pluviometricChart')}}
                    </a>
                    <p v-else class="mb-0" style="color: #00202F">{{$t('OrganizationData.withoutPluviometricChart')}}</p>
                  </v-card-text>
                </v-card>
              </div>
            </v-flex>
          </template>
          <div v-else class="empty-div">
            <p class="empty-list-text mt-1 mb-0">{{$t('OrganizationData.emptyDevices')}}</p>
          </div>
        </v-layout>
      </div>

      <div id="fields-section">
        <v-row class="section-header pt-5 px-3">
          <p class="section-title">{{$t('OrganizationData.fields')}}</p>
          <a v-if="$store.getters.availableCampaigns.length" @click="addField" class="add-elem-btn px-5">+ {{$t('OrganizationData.addField')}}</a>
        </v-row>
        <v-layout row wrap class="fields-list">
          <template v-if="fields.length">
            <v-flex v-for="field in fields" :key="field.id" xs12 sm6 md3 class="px-3 py-3">
              <v-card class="field-card" @click="manageField(field)">
                <v-card-title>
                  <v-layout>
                    <v-flex xs2>
                      <v-img :src="require('@/assets/min-field.png')"/>
                    </v-flex>
                    <v-flex xs8 class="px-1">
                      <p class="field-header-title ma-0">{{field.name}}</p>
                      <p class="field-header-subtitle ma-0">{{getUserFullName(field.contact)}}</p>
                    </v-flex>
                    <v-flex xs2>
                      <v-menu offset-y left>
                        <template v-slot:activator="{ on }">
                          <v-btn class="menu-btn" dark text icon color="#00202F" v-on="on" @click="openFieldMenu(field)">
                            <v-icon>more_horiz</v-icon>
                          </v-btn>
                        </template>
                        <v-list class="pa-0">
                          <v-list-item @click="editField(field)">
                            <v-list-item-title>{{$t('OrganizationData.edit')}}</v-list-item-title>
                          </v-list-item>
                          <v-divider class="mx-2"></v-divider>
                          <v-list-item @click="deleteFieldHandler(field)">
                            <v-list-item-title style="color: #F93A2F">{{$t('OrganizationData.delete')}}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </v-flex>
                  </v-layout>
                </v-card-title>
                <v-card-text>
                  <p>{{ field.crops && field.crops.length == 1 ? $t('OrganizationData.fieldCropQty', {crops: 1}) : $t('OrganizationData.fieldCropsQty', {crops: !field.crops ? 0 : field.crops.length}) }}</p>
                </v-card-text>
              </v-card>
            </v-flex>
          </template>
          <div v-else class="empty-div">
            <p class="empty-list-text mt-1 mb-0">{{$t('OrganizationData.emptyFields')}}</p>
          </div>
        </v-layout>
      </div>

      <div id="users-section">
        <v-row class="section-header pt-5 px-3">
          <p class="section-title">{{$t('OrganizationData.users')}}</p>
          <a v-if="$store.getters.availableCampaigns.length" @click="addUser" class="add-elem-btn px-5">+ {{$t('OrganizationData.addUser')}}</a>
        </v-row>
        <v-layout row wrap class="users-list">
          <template v-if="users.length">
            <v-flex v-for="user in users" :key="user.id" xs4 sm3 md2 class="px-0 py-3">
              <v-row>
                <v-col class="text-center px-0">
                  <div class="user-img-div">
                    <div class="user-icon-div">
                      <div v-if="user.photoUrl && !user.profilePic" class="loading-img-div">
                        <v-progress-circular
                          class="v-progress-img"
                          indeterminate
                          color="primary"
                          :size="50"
                        />
                      </div>
                      <v-img
                        v-else
                        @click="editUser(user)"
                        class="user-img"
                        height="80"
                        max-width="80"
                        :src="getUserImg(user)"
                        :lazy-src="require('../../assets/blank.png')"
                      />
                      <div v-if="user.email"  class="user-icon-container">
                        <v-icon class="user-icon" color="blue">mdi-check-decagram</v-icon>
                      </div>
                    </div>
                  </div>
                  <p @click="editUser(user)" class="user-name pt-3">{{user.name}} {{user.lastName}}</p>
                </v-col>
              </v-row>
            </v-flex>
          </template>
          <div v-else class="empty-div">
            <p class="empty-list-text mt-1 mb-0">{{$t('OrganizationData.emptyUsers')}}</p>
          </div>
        </v-layout>
      </div>
    </v-container>
  </div>
</template>

<script>
// queries
import { getFileDownloadURL } from '../../lib/crudOperations/read'
import { deleteField } from '../../lib/crudOperations/delete'
import { completePreDataStep } from '../../lib/crudOperations/write'
import { setProfilePics } from '../../lib/setProfilePics'
import Dialog from '../Shared/Dialog.vue'
import { EQUIPMENT_SUBTYPE, EQUIPMENT_TYPE, EVENTS_TO_TRACK, HUBSPOT_ONBOARDING_STATES } from '../../constants/const'
import { trackEvent } from '../../lib/track-events'
import { hasUserActiveCampaigns, getCampaignIdsWithPendingInitialData, getUserImg, loadCampaignDataToStore, isNearEndOfService } from '../../lib/helpers'
import AddUserDialog from '../Additions/AddUser/AddUserDialog.vue'
import PostponementRequestDialog from './PostponementRequestDialog.vue'
import ServiceExtensionDialog from '../Campaigns/ServiceExtensionDialog.vue'

export default {
  data () {
    return {
      organization: this.$store.getters.userOrganization.name,
      deleteFieldDialog: {
        show: false,
        cardText: '',
        mainBtnText: this.$t('OrganizationData.deleteFieldMainBtnText'),
        secondaryBtnText: this.$t('OrganizationData.deleteFieldSecondaryBtnText'),
        fieldId: ''
      },
      loading: false,
      deviceDetailDialog: false,
      deviceDetailSelected: {},
      EQUIPMENT_TYPE,
      pendingCampaignOnboardingDialog: false,
      campaignIdsWithPendingInitialData: [],
      getUserImg,
      organizationUsers: [],
      addUserDialog: false,
      DEVICES_WITH_MISSING_DATA_TO_SHOW: 5,
      DEVICES_NEAR_END_OF_SERVICE_TO_SHOW: 5,
      DEVICES_WITHOUT_CROPS_TO_SHOW: 5,
      DEVICES_WITHOUT_USERS_TO_SHOW: 5,
      campaignMenuExpanded: false,
      selectedCampaign: null,
      availableCampaigns: [],
      devicesWithPendingPostponementRequests: [],
      serviceExtensionDialog: {
        show: false,
        device: null
      },
      completedOnboarding: false
    }
  },
  components: {
    Dialog,
    AddUserDialog,
    PostponementRequestDialog,
    ServiceExtensionDialog
  },
  async created () {
    setProfilePics()
    window.addEventListener('scroll', this.handleScroll)
    this.checkCampaignOnboarding()
    this.setDevicesWithPendingPostponementRequests()
  },
  mounted () {
    document.getElementById('organization-data').style.paddingTop = `${document.getElementById('header-div').clientHeight}px`
    // Add event listener to close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside)
    // Load campaigns after component is mounted
    this.loadCampaigns()
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.handleScroll)
    document.removeEventListener('click', this.handleClickOutside)
  },
  watch: {
    '$store.getters.user': {
      handler(newUser) {
        if (newUser && newUser.campaigns) {
          // Small delay to ensure data is completely loaded
          this.$nextTick(() => {
            this.loadCampaigns()
          })
        }
      },
      immediate: true
    }
  },
  computed: {
    hasAvailableCampaigns () {
      return this.$store.getters.availableCampaigns.length > 0
    },
    users () {
      return this.$store.getters.users.sort((a, b) => a.name.localeCompare(b.name))
    },
    devices () {
      return this.$store.getters.devices.sort((a, b) => a.campaign.entityName.localeCompare(b.campaign.entityName))
    },
    fields () {
      return this.$store.getters.fields.sort((a, b) => a.name.localeCompare(b.name))
    },
    display () {
      return this.$vuetify.breakpoint.name
    },
    currentCampaignId () {
      return this.selectedCampaign.id
    },
    devicesWithMissingData () {
      return this.devices.filter(dev => !dev.hasCompleteData)
    },
    devicesWithoutAssignedCrops () {
      return this.devices.filter(device => device.campaign.crops.length === 0)
    },
    devicesWithoutAssignedUsers () {
      return this.devices.filter(device => device.users.length === 0)
    },
    limitedDevicesWithoutCrops () {
      return this.devicesWithoutAssignedCrops.slice(0, this.DEVICES_WITHOUT_CROPS_TO_SHOW)
    },
    limitedDevicesWithoutUsers () {
      return this.devicesWithoutAssignedUsers.slice(0, this.DEVICES_WITHOUT_USERS_TO_SHOW)
    },
    limitedDevicesWithMissingData () {
      return this.devicesWithMissingData.slice(0, this.DEVICES_WITH_MISSING_DATA_TO_SHOW)
    },
    devicesNearEndOfService () {
      return this.devices.filter(device => this.isNearEndOfService(device))
    },
    limitedDevicesNearEndOfService () {
      return this.devicesNearEndOfService.slice(0, this.DEVICES_NEAR_END_OF_SERVICE_TO_SHOW)
    },
    isUserAdmin () {
      // Check if there's an impersonated user (virtualization)
      const impersonatedUserId = this.$store.getters.impersonatedUserId

      if (impersonatedUserId) {
        // If there's an impersonated user, check if the real user (from auth) is admin
        const userAuth = this.$store.getters.userAuth
        if (userAuth && userAuth.user && userAuth.user.uid) {
          return true
        }
        return false
      } else {
        // If no impersonation, check if current user is admin
        return this.$store.getters.user && this.$store.getters.user.isAdmin
      }
    }
  },
  methods: {
    getEquipmentType (type) {
      switch (type) {
        case EQUIPMENT_TYPE.PIVOT:
          return this.$t('Config.pivot')
        case EQUIPMENT_TYPE.AF:
          return this.$t('Config.forwardAvance')
        case EQUIPMENT_TYPE.IR:
          return this.$t('Config.irrigationReel')
        default:
          return '-'
      }
    },
    getLinearEquipmentSubtype (subtype) {
      switch (subtype) {
        case EQUIPMENT_SUBTYPE.AF.ONE_WING:
          return this.$t('Config.linearOneWing')
        case EQUIPMENT_SUBTYPE.AF.TWO_WINGS:
          return this.$t('Config.linearTwoWings')
        case EQUIPMENT_SUBTYPE.AF.PIVOTABLE:
          return this.$t('Config.linearPivotable')
        default:
          return '-'
      }
    },
    addDevice () {
      trackEvent(EVENTS_TO_TRACK.CLICK_ADD_DEVICE_BUTTON)
      this.$router.push({name: 'AddDevice', params: {isNewDevice: true}})
    },
    addField () {
      trackEvent(EVENTS_TO_TRACK.CLICK_ADD_FIELD_BUTTON)
      this.$router.push({name: 'AddField', params: {isNewField: true}})
    },
    addUser () {
      trackEvent(EVENTS_TO_TRACK.CLICK_ADD_USER_BUTTON)
      let users = this.$store.getters.organizationUsers
      // Filter out users that are already in the campaign

      if (this.currentCampaignId) {
        const campaign = this.$store.getters.getCampaign(this.campaignId)
        if (campaign && campaign.users) {
          const campaignUserIds = campaign.users.map(user => user.id || user)
          users = users.filter(user => !campaignUserIds.includes(user.id))
        }
      }
      if (users.length > 0) {
        this.organizationUsers = users
        this.addUserDialog = true
      } else {
        this.$router.push({name: 'AddUser'})
      }
    },
    editDevice (device) {
      this.$router.push({name: 'AddDevice', params: {device, isNewDevice: false}})
    },
    editUser (user) {
      if ((user.photoUrl && user.profilePic) || !user.photoUrl) {
        this.$router.push({name: 'EditUser', params: {user}})
      }
    },
    editField (field) {
      this.$router.push({name: 'AddField', params: {field, isNewField: false}})
    },
    getFieldName (id) {
      if (id) {
        const field = this.fields.find(f => f.id === id)
        return (field && field.name) || '-'
      }
      return '-'
    },
    getDeviceFieldName (device) {
      if (device.campaign && device.campaign.crops && device.campaign.crops.length > 0) {
        // Get the first crop that has a field associated
        const cropWithField = device.campaign.crops.find(crop => crop.field)
        if (cropWithField) {
          return this.getFieldName(cropWithField.field)
        }
      }
      // If no crops or no field in crops, use installationField
      if (device.installationField) {
        return this.getFieldName(device.installationField)
      }
      return '-'
    },
    getUserFullName (id) {
      if (id) {
        const storeUsers = [...this.users, ...this.$store.getters.organizationUsers]
        const user = storeUsers.find(elem => elem.id === id)
        return (user && user.name) ? `${user.name} ${user.lastName || ''}` : '-'
      }
      return '-'
    },
    async downloadPluviometricChart (path, dev) {
      trackEvent(EVENTS_TO_TRACK.DOWNLOAD_PLUVIOMETRIC_CHART, {deviceId: dev.id, deviceName: dev.campaign.entityName})
      try {
        const url = await getFileDownloadURL(path)
        window.open(url, '_blank')
      } catch (error) {
        this.$root.snack.show({text: this.$t('UI.serverError'), error: true})
      }
    },
    deleteFieldHandler (field) {
      if (field.crops && field.crops.length) {
        this.$root.snack.show({text: this.$t('OrganizationData.removeFieldWithCrops'), error: true})
      } else {
        this.deleteFieldDialog.cardText = this.$t('OrganizationData.deleteCropCardText', [field.name])
        this.deleteFieldDialog.show = true
        this.deleteFieldDialog.fieldId = field.id
      }
    },
    async deleteField () {
      try {
        this.loading = true
        await deleteField(this.deleteFieldDialog.fieldId)
        this.$root.snack.show({text: this.$t('UI.deleteSuccess')})
      } catch (err) {
        this.$root.snack.show({text: this.$t('UI.deleteError'), error: true})
      } finally {
        this.loading = false
      }
    },
    manageField (field) {
      trackEvent(EVENTS_TO_TRACK.CLICK_OPEN_FIELD_BUTTON, {fieldId: field.id, fieldName: field.name})
      this.$router.push({name: 'ManageField', params: {field}})
    },
    goToSection (section) {
      const element = document.getElementById(`${section}-section`)
      const toolbarHeight = document.getElementById('toolbar').clientHeight
      const headerHeight = document.getElementById('header-div').clientHeight
      var headerOffset = toolbarHeight + headerHeight
      var elementPosition = element.getBoundingClientRect().top
      var offsetPosition = elementPosition + window.scrollY - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    },
    isInViewport (el) {
      const toolbarHeight = document.getElementById('toolbar').clientHeight
      const headerHeight = document.getElementById('header-div').clientHeight
      const rect = el.getBoundingClientRect()
      return (
        ((rect.top >= (toolbarHeight + headerHeight + 10) && rect.top <= (window.innerHeight || document.documentElement.clientHeight)) ||
        (rect.bottom >= (toolbarHeight + headerHeight + 10) && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight))) ||
        rect.top < (toolbarHeight + headerHeight + 10) && rect.bottom > (window.innerHeight || document.documentElement.clientHeight)
      )
    },
    handleScroll (event) {
      event.preventDefault()
      const devicesSection = document.getElementById('devices-section')
      const fieldsSection = document.getElementById('fields-section')
      const usersSection = document.getElementById('users-section')
      if (this.isInViewport(devicesSection)) {
        document.getElementById('devices-section-menu').style.textDecoration = 'underline'
        document.getElementById('fields-section-menu').style.textDecoration = 'none'
        document.getElementById('users-section-menu').style.textDecoration = 'none'
      } else if (this.isInViewport(fieldsSection)) {
        document.getElementById('fields-section-menu').style.textDecoration = 'underline'
        document.getElementById('devices-section-menu').style.textDecoration = 'none'
        document.getElementById('users-section-menu').style.textDecoration = 'none'
      } else if (this.isInViewport(usersSection)) {
        document.getElementById('users-section-menu').style.textDecoration = 'underline'
        document.getElementById('devices-section-menu').style.textDecoration = 'none'
        document.getElementById('fields-section-menu').style.textDecoration = 'none'
      }
    },
    openDeviceDetailsDialog (device) {
      trackEvent(EVENTS_TO_TRACK.CLICK_OPEN_DEVICE_DETAILS_DIALOG, {deviceId: device.id, deviceName: device.campaign.entityName})
      this.deviceDetailSelected = device
      this.deviceDetailDialog = true
    },
    canEditDevice (device) {
      if (device.isDemoDevice && !this.$store.getters.devUsers.includes(this.$store.getters.userAuth.user.email)) {
        return false
      }

      return true
    },
    openDeviceMenu (device) {
      trackEvent(EVENTS_TO_TRACK.CLICK_ORGANIZATION_HOME_DEVICE_MENU, {deviceId: device.id, deviceName: device.campaign.entityName})
    },
    openFieldMenu (field) {
      trackEvent(EVENTS_TO_TRACK.CLICK_ORGANIZATION_HOME_FIELD_MENU, {fieldId: field.id, fieldName: field.name})
    },
    checkCampaignOnboarding () {
      const hasActiveCampaigns = hasUserActiveCampaigns(this.$store.getters.user.campaigns, this.$store.getters.user.userCampaignsSettings)
      const campaignIdsWithPendingInitialData = getCampaignIdsWithPendingInitialData(this.$store.getters.user.campaigns, this.$store.getters.user.userCampaignsSettings)
      this.campaignIdsWithPendingInitialData = campaignIdsWithPendingInitialData
      if (!campaignIdsWithPendingInitialData.length) return

      if (hasActiveCampaigns) {
        if (!this.$store.getters.pendingCampaignOnboardingDialogShown) {
          this.pendingCampaignOnboardingDialog = true
          this.$store.commit('setPendingCampaignOnboardingDialogShown', true)
        }
      } else {
        this.$router.push({name: 'NewCampaignInitialData', params: {campaignId: campaignIdsWithPendingInitialData[0]}})
      }
    },
    pendingCampaignOnboardingRemindMeLater () {
      trackEvent(EVENTS_TO_TRACK.CLICK_PENDING_CAMPAIGN_ONBOARDING_REMIND_ME_LATER_BUTTON, {
        campaignId: this.campaignIdsWithPendingInitialData[0]
      })
      this.pendingCampaignOnboardingDialog = false
    },
    pendingCampaignOnboardingConfirm () {
      trackEvent(EVENTS_TO_TRACK.CLICK_PENDING_CAMPAIGN_ONBOARDING_START_BUTTON, {
        campaignId: this.campaignIdsWithPendingInitialData[0]
      })
      this.pendingCampaignOnboardingDialog = false
      this.$router.push({name: 'NewCampaignInitialData', params: {campaignId: this.campaignIdsWithPendingInitialData[0]}})
    },
    showFinishCampaignOnboardingCard () {
      const shouldShow = this.hasAvailableCampaigns && (
        this.devices.some(dev => !dev.hasCompleteData) ||
        this.devicesWithoutAssignedCrops.length ||
        this.devicesWithoutAssignedUsers.length
      )

      this.completeOnboarding(!shouldShow)
      return shouldShow
    },
    formatCampaignDate (startDate, endDate) {
      if (!startDate) return ''

      try {
        let start = startDate.toDate()

        const language = this.$i18n.locale || 'es'
        const locale = language === 'es' ? 'es-ES' : 'en-US'

        const formattedStartDate = start.toLocaleDateString(locale, {
          month: 'short',
          year: 'numeric'
        })

        // Capitalize the first letter of the month
        const formattedStart = formattedStartDate.charAt(0).toUpperCase() + formattedStartDate.slice(1)

        // If there's no end date, only show the start date
        if (!endDate) {
          return formattedStart
        }

        let end = endDate.toDate()
        const formattedEndDate = end.toLocaleDateString(locale, {
          month: 'short',
          year: 'numeric'
        })

        // Capitalize the first letter of the end month
        const formattedEnd = formattedEndDate.charAt(0).toUpperCase() + formattedEndDate.slice(1)

        return `${formattedStart}/${formattedEnd}`
      } catch (error) {
        console.error('Error formatting date:', error, startDate, endDate)
        return ''
      }
    },
    toggleCampaignMenu () {
      this.campaignMenuExpanded = !this.campaignMenuExpanded
    },
    async loadCampaigns () {
      try {
        this.availableCampaigns = this.$store.getters.availableCampaigns

        if (this.availableCampaigns.length > 0) {
          // Try to restore the previously selected campaign from store
          const activeCampaignId = this.$store.getters.activeCampaign
          const previouslySelectedCampaign = this.availableCampaigns.find(campaign => campaign.id === activeCampaignId)

          if (previouslySelectedCampaign) {
            // Restore the previously selected campaign
            this.selectedCampaign = previouslySelectedCampaign
          } else {
            // If no previously selected campaign or it's not available, select the first one
            this.selectedCampaign = this.availableCampaigns[0]
            this.$store.commit('setActiveCampaign', this.selectedCampaign.id)
          }
        } else {
          console.warn('No campaigns available')
        }
      } catch (error) {
        console.error('Error loading campaigns:', error)
        this.$root.snack.show({text: this.$t('UI.serverError'), error: true})
      }
    },
    async selectCampaign (campaign) {
      this.selectedCampaign = campaign
      this.campaignMenuExpanded = false
      this.$store.commit('setActiveCampaign', campaign.id)

      // Reload campaign data when campaign changes
      await this.loadCampaignData(campaign.id)
      setProfilePics()
    },
    async loadCampaignData (campaignId) {
      try {
        await loadCampaignDataToStore(campaignId)
        // Update devices with pending postponement requests
        this.setDevicesWithPendingPostponementRequests()
      } catch (error) {
        console.error('Error loading campaign data:', error)
        this.$root.snack.show({text: this.$t('UI.serverError'), error: true})
      }
    },
    handleClickOutside (event) {
      const campaignSelector = event.target.closest('.campaign-selector-container')
      const campaignMenu = event.target.closest('.campaign-menu')

      if (!campaignSelector && !campaignMenu) {
        this.campaignMenuExpanded = false
      }
    },
    editCampaign () {
      if (this.selectedCampaign) {
        trackEvent(EVENTS_TO_TRACK.CLICK_EDIT_CAMPAIGN_BUTTON, {
          campaignId: this.selectedCampaign.id,
          campaignName: this.selectedCampaign.name
        })
        this.$router.push({
          name: 'EditCampaignDevicesAndServices',
          params: {
            campaignId: this.selectedCampaign.id
          }
        })
      }
    },
    setDevicesWithPendingPostponementRequests () {
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      this.devicesWithPendingPostponementRequests = this.devices.filter((device) => {
        const hasPendingPostponement = device.postponementRequest && !device.postponementRequest.accepted
        const hasInactiveModem = device.modem && !device.modem.isActivated
        const hasValidEndDate = device.serviceEndDate instanceof Date && !isNaN(device.serviceEndDate.getTime())

        if (!hasValidEndDate) return false

        const serviceEndDate = new Date(device.serviceEndDate)
        serviceEndDate.setHours(0, 0, 0, 0)

        return hasPendingPostponement && hasInactiveModem && today < serviceEndDate
      })
    },
    isNearEndOfService (device) {
      return isNearEndOfService(device, this.$store.getters.globalConstants)
    },
    queryServiceExtension (device) {
      trackEvent(EVENTS_TO_TRACK.CLICK_REQUEST_SERVICE_EXTENSION_BUTTON, {
        deviceId: device.id
      })
      this.serviceExtensionDialog = {
        ...this.serviceExtensionDialog,
        show: true,
        device
      }
    },
    completeOnboarding (finishConfiguration) {
      if (finishConfiguration && !this.completedOnboarding && this.selectedCampaign && this.selectedCampaign.hubspotOnboardingState !== HUBSPOT_ONBOARDING_STATES.COMPLETED_ONBOARDING) {
        this.completedOnboarding = true
        completePreDataStep(this.selectedCampaign.id, HUBSPOT_ONBOARDING_STATES.COMPLETED_ONBOARDING)
      }
    },
    deviceActivePluviometricChartFile (device) {
      if (!device.pluviometricCharts || device.pluviometricCharts.length === 0) {
        return null
      }
      
      // First, try to find an active chart with linkToFile
      const activeChart = device.pluviometricCharts.find(chart => chart.isActive && chart.linkToFile)
      if (activeChart) {
        return activeChart.linkToFile
      }
      
      // If no active chart, return the first chart that has a linkToFile
      const chartWithFile = device.pluviometricCharts.find(chart => chart.linkToFile)
      return chartWithFile ? chartWithFile.linkToFile : null
    }
  }
}
</script>



// WEBPACK FOOTER //
// OrganizationHome.vue?03cc1066